"use client"

import { type AddressVerificationCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/addressVerificationCopy"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Modal from "@/app/(site)/_components/Atoms/Modal"
import ButtonElement from "@/app/(site)/_components/GCMolecules/ButtonElement"
import { canUseAddressAsEntered } from "@/lib/loqate/canUseAddressAsEntered"
import { clsx } from "@/src/utils/clsx"
import { type AddressVerificationResult } from "@/types/loqate/verify"

interface AddressVerificationModalProps {
    result: AddressVerificationResult | null
    /** Wording for the current language, with any Sanity overrides applied. */
    copy: AddressVerificationCopy
    /** The address as typed, one line per row of a printed address. */
    enteredAddressLines: string[]
    onKeepEntered: () => void
    onEditAddress: () => void
    /** Runs once the dialog releases focus, so the caller can place it deliberately. */
    onClosed: () => void
}

const alertTitleClasses = "Trade_Gothic_Text_16_150_0_700 text-col-text-alert"
const sectionTitleClasses = "Trade_Gothic_Text_16_150_0_700 text-col-text-primary"
const panelTextClasses = "Trade_Gothic_Text_14_150_0_300 text-col-text-primary"
const panelValueClasses = "block text-col-text-alert Trade_Gothic_Text_14_150_0_300"

const panelClasses = "flex flex-col justify-center gap-8 self-stretch bg-col-bg-calm px-8"
const addressPanelClasses = clsx(panelClasses, "items-start py-8")
const issuePanelClasses = clsx(panelClasses, "items-start py-12")

export default function AddressVerificationModal({
    result,
    copy,
    enteredAddressLines,
    onKeepEntered,
    onEditAddress,
    onClosed
}: AddressVerificationModalProps) {
    const isOpen = result?.decision === "fix"

    return (
        <Modal open={isOpen} onOpenChange={open => !open && onEditAddress()}>
            <Modal.Content
                className="temp-md:m-24 temp-md:w-448 temp-md:p-16"
                dismissOnOutsideClick={false}
                onCloseFocus={onClosed}
            >
                <div className="flex items-center justify-between">
                    <Modal.Title className={alertTitleClasses}>
                        {copy.couldNotVerifyTitle}
                    </Modal.Title>
                    <Modal.Close
                        aria-label={copy.editAddressButton}
                        className="flex h-20 w-20 shrink-0 items-center justify-center"
                    >
                        <IconClose className="h-10 w-10" />
                    </Modal.Close>
                </div>

                <Modal.Description className={clsx("pb-24", panelTextClasses)}>
                    {copy.couldNotVerifyDescription}
                </Modal.Description>

                <div className="flex flex-col gap-16 pb-32">
                    <div className="flex flex-col gap-4">
                        <span className={sectionTitleClasses}>{copy.youEntered}</span>
                        <div className={addressPanelClasses}>
                            {enteredAddressLines.map(line => (
                                <span key={line} className={panelTextClasses}>
                                    {line}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <span className={sectionTitleClasses}>
                            {copy.verificationIssuesLabel}
                        </span>
                        <ul className={issuePanelClasses}>
                            {result?.issues.map(item => (
                                <li key={item.code}>
                                    <span className={clsx(panelTextClasses, "block")}>
                                        {copy.issues[item.code]}
                                    </span>
                                    {item.value ? (
                                        <span className={panelValueClasses}>
                                            {item.value}
                                        </span>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="flex flex-col gap-8 pb-12">
                    <ButtonElement
                        text={copy.editAddressButton}
                        onClick={onEditAddress}
                        size={{ desktop: "large", mobile: "small" }}
                        theme="primary-black"
                        className="w-full justify-center"
                    />
                    {result && canUseAddressAsEntered(result) ? (
                        <ButtonElement
                            text={copy.useAsEnteredButton}
                            onClick={onKeepEntered}
                            size={{ desktop: "large", mobile: "small" }}
                            theme="tertiary-black"
                            className="w-full justify-center"
                        />
                    ) : null}
                </div>
            </Modal.Content>
        </Modal>
    )
}
